import { Header } from "./components/Header";
import { Hero } from "./components/Hero";
import { Features } from "./components/Features";
import { Pricing } from "./components/Pricing";
import { Footer } from "./components/Footer";
import { ActionFunctionArgs, redirect } from "@remix-run/node";
import { LoaderFunctionArgs } from "@remix-run/node";
import { login } from "../../shopify.server";
import {
  ArrowAnimation,
  PinterestPreviews,
  ShopifyPreview,
} from "./components/HeroVisuals";
import { contactUsTable, drizzleDb } from "vitamin-db";
import { useFetcherWithPromise } from "@/hooks/useFetcherWithPromise";
import { motion } from "framer-motion";

export const action = async ({ request }: ActionFunctionArgs) => {
  try {
    const formData = await request.formData();
    const email = formData.get("email");

    await drizzleDb.insert(contactUsTable).values({
      email: email as string,
      phone: null,
      message: "",
      createdAt: new Date().toISOString(),
      id: crypto.randomUUID(),
    });
    return {
      message: "Thank you for your interest! We'll notify you when we launch.",
    };
  } catch (err: any) {
    return {
      error: `Something went wrong. Please try again later. ${err.message}`,
    };
  }
};

/**
 * Loader to redirect to the app if the shop query param is provided. (Standard when the app is embedded in shopify admin)
 */
export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);

  if (url.searchParams.get("shop")) {
    throw redirect(`/app?${url.searchParams.toString()}`);
  }

  return { showForm: Boolean(login) };
};

export default function App() {
  const fetcher = useFetcherWithPromise<typeof action>();

  return (
    <div className="flex flex-col min-h-screen overflow-x-hidden">
      <Header />
      <main className="flex-grow">
        <Hero />
        <Features />
        <motion.section
          className="py-12 sm:py-16 bg-gray-50"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
            initial={{ y: 20 }}
            whileInView={{ y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <motion.h2
              className="text-xl sm:text-2xl font-semibold text-center mb-3 sm:mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              Turn Your Store Products into Pinterest-Ready Content
            </motion.h2>
            <motion.p
              className="text-sm text-gray-600 text-center mb-8 sm:mb-12 max-w-2xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Automatically convert your Shopify product listings into
              eye-catching Pinterest pins that drive traffic back to your store.
              Our tool takes your product images, descriptions, and pricing to
              create Pinterest-optimized content that reaches new customers.
            </motion.p>
            <motion.div
              className="space-y-12"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <div className="mx-auto relative overflow-hidden px-4 sm:px-6 lg:px-8">
                <div className="relative w-full max-w-3xl h-[470px] sm:h-[500px] md:h-[550px] py-4 sm:py-8 mx-auto">
                  <ShopifyPreview />
                  <ArrowAnimation />
                  <PinterestPreviews />
                </div>
              </div>
            </motion.div>
          </motion.div>
        </motion.section>
        <Pricing />
      </main>
      <Footer />

    </div>
  );
}
